import { inject } from '@angular/core';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import { ConstructionProjectFeaturesAccessMap, ELEMENT_TYPE_TO_MAP_LAYER } from '@construction/construction.enums';
import {
  ConstructionProjectFeaturesType,
  ConstructionProjectLoadModel,
  QCScope,
  QCType,
} from '@construction/models/construction-project.model';
import { ConstructionProjectFeatureBuilderService } from '@construction/services/construction-project-feature-builder.service';

export function normalizeWeightEntries(
  weights: Record<string, number | null> | undefined,
): Array<[string, number | null]> {
  const normalized = new Map<string, number | null>();

  for (const [key, value] of Object.entries(weights ?? {})) {
    const layer = ELEMENT_TYPE_TO_MAP_LAYER[key] ?? key;
    const existing = normalized.get(layer);

    if (typeof existing === 'number' && typeof value === 'number') {
      normalized.set(layer, existing + value);
    } else if (existing === undefined || value !== null) {
      normalized.set(layer, value);
    }
  }

  return [...normalized.entries()];
}

/** Depends on all upstream state slices. `updateConstructionProjectStore` is the single entry point to hydrate the store after a project load. */
export function withHydration() {
  return signalStoreFeature(
    {
      state: type<{
        project: ConstructionProjectLoadModel;
        lastEventStatus: string | undefined;
        features: ConstructionProjectFeaturesType[];
        featurePermission: ConstructionProjectFeaturesAccessMap;
        qcTypes: QCType[];
        qcScopes: QCScope[];
        qcConfigPublished: boolean;
        visibleElements: string[];
        elementsWithWeights: string[];
      }>(),
    },
    withMethods(function hydrationMethods(store) {
      const featureBuilderService = inject(ConstructionProjectFeatureBuilderService);

      return {
        /**
         * Main hydration method — call this whenever a project is loaded or refreshed.
         * Populates all state slices in one pass.
         */
        updateConstructionProjectStore(constructionProject: ConstructionProjectLoadModel): void {
          const weightEntries = normalizeWeightEntries(constructionProject.weights);
          const visibleWeightEntries = weightEntries.filter(([, value]) => value !== null);
          const featurePermission = featureBuilderService.buildPermissionMapForFeatures({
            epcCompanyId: constructionProject.epc_company ?? '',
            ownerCompanyId: constructionProject.owner_company ?? '',
            supplierCompanyId: constructionProject.supplier_company ?? '',
            featureFlags: constructionProject.feature_flags,
            team: constructionProject.team ?? [],
          });

          patchState(store, {
            project: constructionProject,
            lastEventStatus: constructionProject.last_event_status?.status,
            features: constructionProject.feature_flags,
            qcTypes: constructionProject.qc_config?.qc_types ?? [],
            qcScopes: constructionProject.qc_config?.qc_scopes ?? [],
            qcConfigPublished: !!constructionProject.qc_config?.published_at,
            visibleElements: visibleWeightEntries.map(([key]) => key),
            elementsWithWeights: visibleWeightEntries.filter(([, value]) => (value as number) > 0).map(([key]) => key),
            featurePermission,
          });
        },
      };
    }),
  );
}
